import {
  ClockIcon,
  TrashIcon,
  XCircleIcon,
  CreditCardIcon,
  ArrowPathIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { ILead, ILeadAction, LeadActionType } from "../../models/Lead";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useLeadActions from "../../data/useLeadActions";
import { useUserLookup } from "../../hooks/useUserLookup";


export const getActionMeta = (actionType: LeadActionType | null | undefined) => {
  switch (actionType) {
    case LeadActionType.Generic:
      return {
        label: "Deleted",
        icon: TrashIcon,
        dotClass: "bg-red-400",
        badgeClass: "bg-red-50 text-red-700 ring-red-200",
      };
    case LeadActionType.Cancelled:
      return {
        label: "Cancelled",
        icon: XCircleIcon,
        dotClass: "bg-orange-400",
        badgeClass: "bg-orange-50 text-orange-700 ring-orange-200",
      };
    case LeadActionType.PaymentStatusChanged:
      return {
        label: "Payment Status",
        icon: CreditCardIcon,
        dotClass: "bg-emerald-400",
        badgeClass: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      };
    case LeadActionType.StatusChanged:
      return {
        label: "Status Changed",
        icon: ArrowPathIcon,
        dotClass: "bg-blue-400",
        badgeClass: "bg-blue-50 text-blue-700 ring-blue-200",
      };
    case LeadActionType.CallListStatusChanged:
      return {
        label: "Call List Status",
        icon: PhoneIcon,
        dotClass: "bg-violet-400",
        badgeClass: "bg-violet-50 text-violet-700 ring-violet-200",
      };
    case LeadActionType.PriceChanged:
      return {
        label: "Price Changed",
        icon: CurrencyDollarIcon,
        dotClass: "bg-yellow-400",
        badgeClass: "bg-yellow-50 text-yellow-700 ring-yellow-200",
      };
    case LeadActionType.Generic:
    default:
      return {
        label: "General",
        icon: ChatBubbleLeftRightIcon,
        dotClass: "bg-gray-300",
        badgeClass: "bg-gray-50 text-gray-700 ring-gray-200",
      };
  }
};

export default function LeadActions({ lead }: { lead: ILead }) {
  const { t } = useTranslation("leads");
  const [actions, setActions] = useState<ILeadAction[]>([]);
  const { leadActions } = useLeadActions({ leadId: lead.id });
  const { findUser } = useUserLookup();

  useEffect(() => {
    if (leadActions.isLoading || !leadActions.data?.data) return;
    setActions(leadActions.data.data);
  }, [leadActions.isLoading, leadActions.data]);

  const getActionedByLabel = (actionedBy?: string | null) => {
    if (!actionedBy) return t("System");
    const user = findUser(actionedBy);
    if (!user) return t("Unknown user");
    return user.fullname || user.email || user.username || t("Unknown user");
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-4">
        <ClockIcon className="h-5 w-5 text-gray-400" />
        <h3 className="text-sm font-semibold text-gray-900">{t("Lead Actions")}</h3>
      </div>

      {actions.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center">
          <p className="text-sm font-medium text-gray-700">{t("No lead actions yet")}</p>
          <p className="text-xs text-gray-500 mt-1">
            {t("Actions like status changes, cancellations, and payment updates will appear here.")}
          </p>
        </div>
      ) : (
        <div className="space-y-0">
          {actions.map((action, idx) => {
            const date = new Date(action.created);
            const timeStr = date.toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            });
            const dateStr = date.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            });

            const meta = getActionMeta(action.actionType);
            const Icon = meta.icon;
            const isSystem = !action.actionedBy;

            return (
              <div
                key={action.id}
                className="flex gap-3 py-3 border-b border-gray-100 last:border-0"
              >
                <div className="flex flex-col items-center pt-1">
                  <div className={`h-2.5 w-2.5 rounded-full ${meta.dotClass}`} />
                  {idx < actions.length - 1 && (
                    <div className="w-px flex-1 bg-gray-200 mt-1.5" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5">
                          <Icon className="h-4 w-4 text-gray-400" />
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {action.description || t(meta.label)}
                          </p>
                        </div>

                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${meta.badgeClass}`}
                        >
                          {t(meta.label)}
                        </span>
                      </div>
                    </div>

                    <span className="text-[11px] text-gray-400 whitespace-nowrap">
                      {timeStr}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5 mt-1">
                    <span
                      className={`text-[11px] font-medium ${
                        isSystem ? "text-gray-400" : "text-primary-600"
                      }`}
                    >
                      {getActionedByLabel(action.actionedBy)}
                    </span>
                    <span className="text-[10px] text-gray-300">·</span>
                    <span className="text-[11px] text-gray-400">{dateStr}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}